import { EquipmentTypeBadge } from '@/components/badges/EquipmentTypeBadge';
import { Input } from '@/components/ui/input';
import { allEquipmentTypesState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { type EquipmentTypeId, isOfferableType, type SpaceId } from '@rock/types';
import { type FC, useMemo, useState } from 'react';

/**
 * Type-to-filter picker over the equipment-type catalog that adds one blue id pill per selection — the
 * same add-a-chip shape as the prefix list beside it. A full checkbox grid of every type would out-size
 * the handful a document actually covers.
 *
 * Spans EVERY space, ranking the publishing model's own space first. A type's `spaceId` is a catalog
 * grouping, not a fence: a garage-mounted tankless water heater is a `plumbing` type, and a document
 * matches on its `typeIds` alone, so a space-filtered picker could only ever hide types the rule would
 * have matched. Each option carries its space so crossing one stays a visible choice, not an accident.
 */
export const EquipmentTypeMultiSelect: FC<{
    /** The publishing model's space — ORDERS the options, never filters them. */
    spaceId: SpaceId | undefined;
    value: EquipmentTypeId[];
    onChange: (next: EquipmentTypeId[]) => void;
    /** Types shown without a remove button: the form owns why they must stay. */
    locked?: readonly EquipmentTypeId[];
    disabled?: boolean;
}> = ({ spaceId, value, onChange, locked = [], disabled }) => {
    const equipmentTypes = useAsyncAtomValue(allEquipmentTypesState, []);
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);

    const matches = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return equipmentTypes
            .filter((type) => !value.includes(type.id) && isOfferableType(type))
            .filter((type) => !needle || type.id.includes(needle) || type.title.toLowerCase().includes(needle))
            .sort((a, b) => {
                // The model's own space first — the common case stays a one-keystroke pick even though
                // the whole catalog is now in reach.
                const home = Number(b.spaceId === spaceId) - Number(a.spaceId === spaceId);
                return home || a.title.localeCompare(b.title);
            })
            .slice(0, 8);
    }, [equipmentTypes, value, query, spaceId]);

    const add = (typeId: EquipmentTypeId) => {
        if (!value.includes(typeId)) onChange([...value, typeId]);
        setQuery('');
        setOpen(false);
    };

    return (
        <div className="flex flex-wrap items-center gap-1.5">
            {value.map((typeId) => (
                <EquipmentTypeBadge
                    key={typeId}
                    typeId={typeId}
                    disabled={disabled}
                    onRemove={locked.includes(typeId) ? undefined : () => onChange(value.filter((id) => id !== typeId))}
                />
            ))}
            <div className="relative">
                <Input
                    value={query}
                    onChange={(event) => {
                        setQuery(event.target.value);
                        setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                    // Blur closes on the next tick so a click on an option still lands (the options
                    // themselves also suppress the blur via onMouseDown).
                    onBlur={() => setTimeout(() => setOpen(false), 0)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' && matches[0]) {
                            event.preventDefault();
                            add(matches[0].id);
                        }
                        if (event.key === 'Escape') setOpen(false);
                    }}
                    placeholder="Add type…"
                    disabled={disabled}
                    autoComplete="off"
                    className="h-7 w-40"
                />
                {open && matches.length > 0 && (
                    <div className="absolute left-0 top-8 z-50 max-h-56 w-72 overflow-auto rounded-md border bg-popover p-1 shadow-md">
                        {matches.map((type) => (
                            <button
                                key={type.id}
                                type="button"
                                // Fire before blur, so the pick isn't lost to the input closing first.
                                onMouseDown={(event) => {
                                    event.preventDefault();
                                    add(type.id);
                                }}
                                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
                            >
                                <span className="truncate">{type.title}</span>
                                {/* Only for types from ANOTHER space — on the model's own space it would
                                    be noise on every row, and it is the exception that needs a look. */}
                                {type.spaceId !== spaceId && (
                                    <span className="shrink-0 rounded bg-muted px-1 text-[10px] text-muted-foreground">
                                        {type.spaceId}
                                    </span>
                                )}
                                <span className="ml-auto shrink-0 text-xs text-muted-foreground">{type.id}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
